import React from 'react';

import { graphql, useFragment } from 'react-relay';

import { type ProductKeys } from '../../../../common/constants/products';
import type { ThirdPartyConfigsBootstrap } from '../../../../common/constants/schemas/3p-config';
import type { SearchResultSharedProps } from '../../../../common/types';
import { useAppContext } from '../../../../common/ui/app-context';
import { isLeapQueryEnabled } from '../../../../common/utils/leap-query';
import { ThirdPartyQuerySuggestionsContainer } from '../../../../common/utils/third-party-query-suggestions-container';
import { useAnalytics } from '../../../../controllers/analytics';
import { ErrorBoundary } from '../../../../controllers/error-boundary';
import { useIsUserOAuthed } from '../../../../controllers/oauth';
import { useSearchActions } from '../../../../controllers/store/search';

import type { searchResult_AtlassianSearchPage$key } from './__generated__/searchResult_AtlassianSearchPage.graphql';
import { BulkConnectBanner } from './bulk-connect-banner';
import { LeapQueryBanner } from './leap-query-banner';
import { SearchResultRenderer } from './search-result-renderer';
import type { JiraIssueStatusAndColorName } from './search-result-renderer/search-jira-issue';

// How many search results to show before the OAuth banner
export const OAUTH_BANNER_POSITION = 4;
const LEAP_QUERY_BANNER_POSITION = 5;
const THIRD_PARTY_QUERY_SUGGESTION_POSITION = 5;

export type SearchResultProps = SearchResultSharedProps & {
	node: searchResult_AtlassianSearchPage$key;
	position: number;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	availableProducts: ProductKeys[];
	originalQuery: string | null | undefined;
	logError: (error: any) => Promise<void>;
	compassTeamName?: string | null; // Optional, used for Compass teams
	jiraIssueStatus?: JiraIssueStatusAndColorName | null; // Optional, used for Jira issues
	is3pGroupingEnabled?: boolean;
	smartAnswersPortalRef?: React.RefObject<HTMLDivElement>;
	mainContentWidth?: number;
};

export const SearchResult = ({
	node,
	position,
	thirdPartyConfigs,
	availableProducts,
	cloudId,
	searchQuery,
	originalQuery,
	selectedProduct,
	searchSessionId,
	primaryProduct,
	appMode,
	logError,
	compassTeamName,
	jiraIssueStatus,
	is3pGroupingEnabled,
	smartAnswersPortalRef,
	mainContentWidth,
}: SearchResultProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchResult_AtlassianSearchPage on SearchResult
			@argumentDefinitions(
				includeAssigneeField: { type: "Boolean", defaultValue: false }
				includeIssueTypeField: { type: "Boolean", defaultValue: false }
				includeIconUrl: { type: "Boolean", defaultValue: false }
				includeAssets: { type: "Boolean", defaultValue: false }
				includePassionfruitAsk: { type: "Boolean", defaultValue: false }
				includePositions: { type: "Boolean", defaultValue: false }
				includeThirdPartyId: { type: "Boolean", defaultValue: false }
				includeNavBoostScore: { type: "Boolean", defaultValue: false }
			) {
				id
				...searchResultRenderer_AtlassianSearchPage
					@arguments(
						includeAssigneeField: $includeAssigneeField
						includeIssueTypeField: $includeIssueTypeField
						includeIconUrl: $includeIconUrl
						includeAssets: $includeAssets
						includePassionfruitAsk: $includePassionfruitAsk
						includePositions: $includePositions
						includeThirdPartyId: $includeThirdPartyId
						includeNavBoostScore: $includeNavBoostScore
					)
			}
		`,
		node,
	);

	const { isAdminHubAIEnabled, isRovoLLMEnabled } = useAppContext();

	return (
		<>
			{position === OAUTH_BANNER_POSITION && (
				<BulkConnectBanner
					thirdPartyConfigs={thirdPartyConfigs}
					selectedProduct={selectedProduct}
					availableProducts={availableProducts}
				/>
			)}
			{position === LEAP_QUERY_BANNER_POSITION &&
				isLeapQueryEnabled(appMode, isAdminHubAIEnabled, isRovoLLMEnabled) && (
					<LeapQueryBanner
						searchQuery={searchQuery}
						originalQuery={originalQuery}
						primaryProduct={primaryProduct}
						logError={logError}
					/>
				)}
			{position === THIRD_PARTY_QUERY_SUGGESTION_POSITION && !selectedProduct && (
				<ThirdPartyQuerySuggestionsContainer
					thirdPartyConfigs={thirdPartyConfigs}
					useIsUserOAuthed={useIsUserOAuthed}
					useSearchActions={useSearchActions}
					useAnalytics={useAnalytics}
					currentQuery={searchQuery}
				/>
			)}
			<ErrorBoundary section="SearchPageResultRecord">
				<SearchResultRenderer
					key={data.id}
					node={data}
					position={position}
					cloudId={cloudId}
					searchQuery={searchQuery}
					searchSessionId={searchSessionId}
					appMode={appMode}
					primaryProduct={primaryProduct}
					selectedProduct={selectedProduct}
					thirdPartyConfigs={thirdPartyConfigs}
					compassTeamName={compassTeamName}
					jiraIssueStatus={jiraIssueStatus}
					is3pGroupingEnabled={is3pGroupingEnabled}
					smartAnswersPortalRef={smartAnswersPortalRef}
					mainContentWidth={mainContentWidth}
				/>
			</ErrorBoundary>
		</>
	);
};
